import { z } from 'zod';

const PASSWORD_MIN_LENGTH = 8;
const PASSWORD_HAS_LETTER_REGEX = /\p{L}/u;
const PASSWORD_HAS_SPECIAL_REGEX = /[^\p{L}\p{N}\s]/u;

// Login form validation schema
export const loginSchema = z.object({
  email: z
    .string()
    .min(1, 'errors.emailRequired')
    .email('errors.emailInvalid'),
  password: z
    .string()
    .min(1, 'errors.passwordRequired'),
  rememberMe: z.boolean().default(false),
});

export type LoginFormData = z.infer<typeof loginSchema>;

// User creation form validation schema
export const createUserSchema = z.object({
  email: z
    .string()
    .min(1, 'errors.emailRequired')
    .email('errors.emailInvalid'),
  password: z
    .string()
    .min(PASSWORD_MIN_LENGTH, 'errors.passwordTooShort')
    .refine((val) => PASSWORD_HAS_LETTER_REGEX.test(val), 'errors.passwordMissingLetter')
    .refine((val) => PASSWORD_HAS_SPECIAL_REGEX.test(val), 'errors.passwordMissingSpecial'),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  isActive: z.boolean(),
  roleIds: z.array(z.string()).optional(),
});

export type CreateUserFormData = z.infer<typeof createUserSchema>;

// Role creation form validation schema
export const createRoleSchema = z.object({
  name: z
    .string()
    .min(1, 'errors.nameRequired')
    .regex(/^[a-zA-Z0-9_-]+$/, 'errors.nameInvalid'),
  description: z.string().optional(),
  permissions: z
    .array(z.string())
    .min(1, 'errors.permissionsRequired'),
  serviceName: z.string().optional(),
});

export type CreateRoleFormData = z.infer<typeof createRoleSchema>;

// Role edit form validation schema
export const editRoleSchema = z.object({
  name: z
    .string()
    .min(1, 'errors.nameRequired')
    .regex(/^[a-zA-Z0-9_-]+$/, 'errors.nameInvalid'),
  description: z.string().optional(),
  permissions: z
    .array(z.string())
    .min(1, 'errors.permissionsRequired'),
  serviceName: z.string().optional(),
});

export type EditRoleFormData = z.infer<typeof editRoleSchema>;

// User edit form validation schema
export const editUserSchema = z.object({
  email: z
    .string()
    .min(1, 'errors.emailRequired')
    .email('errors.emailInvalid'),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  isActive: z.boolean(),
  // Password is optional for editing - only validate if provided
  password: z
    .string()
    .optional()
    .refine((val) => {
      if (!val || val.length === 0) return true; // Allow empty password (no change)
      return val.length >= PASSWORD_MIN_LENGTH;
    }, 'errors.passwordTooShort')
    .refine((val) => {
      if (!val || val.length === 0) return true; // Allow empty password (no change)
      return PASSWORD_HAS_LETTER_REGEX.test(val);
    }, 'errors.passwordMissingLetter')
    .refine((val) => {
      if (!val || val.length === 0) return true; // Allow empty password (no change)
      return PASSWORD_HAS_SPECIAL_REGEX.test(val);
    }, 'errors.passwordMissingSpecial'),
  confirmPassword: z.string().optional(),
  roleIds: z.array(z.string()).optional(),
}).refine((data) => {
  // If password is provided, confirm password must match
  if (data.password && data.password.length > 0) {
    return data.password === data.confirmPassword;
  }
  return true;
}, {
  message: 'errors.passwordsDoNotMatch',
  path: ['confirmPassword'],
});

export type EditUserFormData = z.infer<typeof editUserSchema>;